import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, Clock3, ChevronDown, MapPin, Receipt, Package, Truck, RotateCcw, Star,
  CreditCard, MessageCircle, HelpCircle, Store, ChevronRight, Loader2,
  ShoppingCart, Ticket, Settings as SettingsIcon, Globe, Smartphone, User as UserIcon, Gavel,
} from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useSiteConfig } from "../context/ConfigContext";
import ProductCard from "../components/ProductCard";
import DownloadApp, { AppleBadge, PlayBadge } from "../components/DownloadApp";

const WISH_KEY = "simashi_wish";
const HIST_KEY = "simashi_history";
const readStore = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; }
};

const ORDER_GROUPS = [
  { key: "unpaid", icon: Receipt, t: "grp_unpaid", match: ["unpaid", "pending_payment", "pending", "awaiting_payment"] },
  { key: "processing", icon: Package, t: "grp_processing", match: ["processing", "confirmed", "paid", "placed", "preparing"] },
  { key: "shipped", icon: Truck, t: "grp_shipped", match: ["shipped", "dispatched", "in_transit", "on_the_way", "out_for_delivery"] },
  { key: "refunds", icon: RotateCcw, t: "grp_refunds", match: ["refund", "refunded", "return", "returned", "cancelled", "canceled", "disputed"] },
  { key: "review", icon: Star, t: "grp_review", match: ["delivered", "completed", "complete", "to_review", "review"] },
];
const matchOrder = (o) => {
  const s = String(o.status || "").toLowerCase();
  const g = ORDER_GROUPS.find((x) => x.match.some((m) => s.includes(m)));
  return g ? g.key : "processing";
};

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.55, delay: d, ease: [0.22, 1, 0.36, 1] },
});

const Section = ({ title, action, children, tid }) => (
  <motion.section data-testid={tid} className="mt-6" {...fade()}>
    <div className="flex items-center justify-between">
      <h2 className="font-heading text-base md:text-lg font-bold text-slate-900">{title}</h2>
      {action}
    </div>
    <div className="mt-3">{children}</div>
  </motion.section>
);

const Badge = ({ n }) =>
  n > 0 ? (
    <span className="absolute -right-2 -top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white shadow">
      {n > 99 ? "99+" : n}
    </span>
  ) : null;

const FeatureTile = ({ icon: Icon, label, tid, ...rest }) => (
  <button
    data-testid={tid}
    {...rest}
    className="flex flex-col items-center gap-2 rounded-2xl px-1 py-3 text-center transition hover:bg-teal-50 active:scale-95"
  >
    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-700/10 text-teal-700">
      <Icon className="h-5 w-5" />
    </span>
    <span className="text-[11px] font-semibold leading-tight text-slate-700">{label}</span>
  </button>
);

export default function Account() {
  const { user, isSeller } = useAuth();
  const { t } = useLang();
  const { features, auction, app_links } = useSiteConfig();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [rfqs, setRfqs] = useState(null);
  const [addresses, setAddresses] = useState(null);
  const [convs, setConvs] = useState(null);
  const [recs, setRecs] = useState([]);
  const [wishItems, setWishItems] = useState([]);
  const [wishCount, setWishCount] = useState(() => readStore(WISH_KEY).length);
  const [histCount, setHistCount] = useState(() => readStore(HIST_KEY).length);

  const [appsOpen, setAppsOpen] = useState(false);

  useEffect(() => {
    api("/products?limit=8").then(setRecs).catch(() => setRecs([]));
    const wish = readStore(WISH_KEY);
    setWishCount(wish.length);
    if (wish.length) {
      Promise.all(wish.slice(0, 6).map((id) => api(`/products/${id}`).catch(() => null))).then((r) =>
        setWishItems(r.filter(Boolean))
      );
    }
    setHistCount(readStore(HIST_KEY).length);
    if (!user) return;
    api("/orders/mine", { auth: true }).then(setOrders).catch(() => setOrders([]));
    api("/rfq/mine", { auth: true }).then(setRfqs).catch(() => setRfqs([]));
    api("/me/addresses", { auth: true }).then(setAddresses).catch(() => setAddresses([]));
    api("/conversations", { auth: true }).then(setConvs).catch(() => setConvs([]));
  }, [user]);

  const groups = useMemo(() => {
    const counts = {};
    let latest = null;
    (orders || []).forEach((o) => {
      const k = matchOrder(o);
      counts[k] = (counts[k] || 0) + 1;
      if (!latest || new Date(o.updated_at || o.created_at || 0) > new Date(latest.updated_at || latest.created_at || 0)) latest = o;
    });
    return { counts, latest };
  }, [orders]);

  const unread = (convs || []).reduce((a, c) => a + (c.unread || 0), 0);
  const addrCount = (addresses || []).length;

  /* ---------- GUEST ---------- */
  if (!user) {
    const guestMenu = [
      { icon: MessageCircle, label: t("tab_messages"), to: "/login?next=/account/messages", tid: "guest-menu-messages" },
      { icon: ShoppingCart, label: t("nav_cart"), to: "/cart", tid: "guest-menu-cart" },
      { icon: Package, label: t("manage_orders"), to: "/login?next=/account/orders", tid: "guest-menu-orders" },
      { icon: Heart, label: t("my_favorites"), to: "/account/favorites", tid: "guest-menu-favorites" },
      { icon: Ticket, label: t("my_coupons"), to: "/login?next=/account", tid: "guest-menu-coupons" },
      ...(auction?.enabled ? [{ icon: Gavel, label: t("nav_auctions"), to: "/auctions", tid: "guest-menu-auctions" }] : []),
      { icon: Store, label: t("start_selling"), to: "/login?next=/seller", tid: "guest-menu-sell" },
      { icon: HelpCircle, label: t("help_t"), to: "/help", tid: "guest-menu-help" },
      { icon: Globe, label: t("clc"), to: "/account/settings", tid: "guest-menu-clc" },
      { icon: SettingsIcon, label: t("settings"), to: "/account/settings", tid: "guest-menu-settings" },
    ];
    return (
      <div data-testid="account-guest" className="mx-auto max-w-3xl px-4 pb-10 sm:px-6">
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          data-testid="guest-title"
          className="font-heading pt-5 text-2xl font-extrabold tracking-tight text-slate-900"
        >
          {t("tab_account")}
        </motion.h1>

        {/* Profile — photo slot ready for when they join */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 flex items-center gap-4"
        >
          <span data-testid="guest-avatar" className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400 ring-1 ring-slate-200">
            <UserIcon className="h-8 w-8" />
          </span>
          <div className="min-w-0 flex-1">
            <p data-testid="guest-name" className="font-heading text-lg font-bold text-slate-900">{t("guest_hi")}</p>
            <p className="text-xs text-slate-500">{t("guest_photo_hint")}</p>
            <div className="mt-2 flex gap-2">
              <Link
                data-testid="guest-login-button"
                to="/login?next=/account"
                className="rounded-full bg-teal-700 px-5 py-2 text-xs font-bold text-white transition hover:bg-teal-800 active:scale-95"
              >
                {t("nav_login")}
              </Link>
              <Link
                data-testid="guest-register-button"
                to="/register"
                className="rounded-full border border-teal-700 px-5 py-2 text-xs font-bold text-teal-700 transition hover:bg-teal-50 active:scale-95"
              >
                {t("nav_join")}
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Left-aligned menu rows */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 divide-y divide-slate-100 rounded-2xl bg-white shadow-sm ring-1 ring-slate-100"
        >
          {guestMenu.map((m) => (
            <Link
              key={m.tid}
              data-testid={m.tid}
              to={m.to}
              className="flex items-center gap-3.5 px-4 py-4 text-left transition hover:bg-teal-50/50 active:scale-[0.99]"
            >
              <m.icon className="h-5 w-5 shrink-0 text-slate-700" strokeWidth={1.8} />
              <span className="flex-1 text-sm font-semibold text-slate-800">{m.label}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
            </Link>
          ))}
          {/* Last row: Marhine.com Apps — expands the store badges below it */}
          <button
            data-testid="guest-menu-apps"
            type="button"
            onClick={() => setAppsOpen((v) => !v)}
            className="flex w-full items-center gap-3.5 px-4 py-4 text-left transition hover:bg-teal-50/50 active:scale-[0.99]"
          >
            <Smartphone className="h-5 w-5 shrink-0 text-slate-700" strokeWidth={1.8} />
            <span className="flex-1 text-sm font-semibold text-slate-800">{t("apps_row")}</span>
            <ChevronRight className={`h-4 w-4 shrink-0 text-slate-300 transition-transform ${appsOpen ? "rotate-90" : ""}`} />
          </button>
          {appsOpen && (
            <div data-testid="guest-apps-panel" className="flex flex-wrap gap-2.5 px-4 pb-5 pt-1">
              <AppleBadge tid="dl-app-store" href={app_links?.ios} />
              <PlayBadge tid="dl-google-play" href={app_links?.android} />
            </div>
          )}
        </motion.div>

        {/* Product lines below the menu */}
        {recs.length > 0 && (
          <motion.section
            data-testid="guest-products"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-base md:text-lg font-bold text-slate-900">{t("inspired")}</h2>
              <Link data-testid="guest-products-all" to="/products" className="flex items-center gap-0.5 text-xs font-semibold text-slate-500 hover:text-teal-700">
                {t("view_all")} <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {recs.slice(0, 6).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </motion.section>
        )}
      </div>
    );
  }

  const initials = (user.name || user.email || "S").slice(0, 1).toUpperCase();
  const deliverTo = addresses?.find((a) => a.is_default) || addresses?.[0];

  return (
    <div data-testid="account-page" className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
      {/* ---------- HERO (masked line reveal) ---------- */}
      <motion.header
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative -mx-4 sm:-mx-6 overflow-hidden bg-gradient-to-b from-teal-50/80 to-transparent px-4 sm:px-6 pb-4 pt-6"
      >
        <span className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-teal-200/40 blur-3xl" />
        <div className="flex items-center gap-3.5">
          <span data-testid="account-avatar" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-teal-700 text-xl font-extrabold text-white ring-4 ring-white shadow-lg">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <span className="block overflow-hidden">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="account-name"
                  className="block truncate font-heading text-xl font-extrabold tracking-tight text-slate-900"
                >
                  {user.company || user.name}
                </motion.span>
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
            </div>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
              <MapPin className="h-3.5 w-3.5" />
              <span className="truncate">
                {t("deliver_to")} {deliverTo ? [deliverTo.city, deliverTo.country].filter(Boolean).join(", ") : user.country || "—"}
              </span>
            </p>
          </div>
          <Link
            data-testid="account-settings-button"
            to="/account/settings"
            aria-label={t("settings")}
            className="shrink-0 rounded-full border border-slate-200 bg-white p-2.5 text-slate-600 shadow-sm transition hover:border-teal-400 hover:text-teal-700 active:scale-90"
          >
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        {/* quick cards: favorite / history */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <Link
            data-testid="account-favorite-card"
            to="/account/favorites"
            className="group flex items-center justify-between rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100 transition hover:ring-teal-300 active:scale-[0.98]"
          >
            <div>
              <p className="text-sm font-bold text-slate-800">{t("favorite")}</p>
              <p className="mt-0.5 text-xs text-slate-400">{wishCount} {t("items_t").toLowerCase()}</p>
            </div>
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-50 text-rose-500 transition group-hover:scale-110">
              <Heart className="h-5 w-5 fill-current" />
            </span>
          </Link>
          <Link
            data-testid="account-history-card"
            to="/account/history"
            className="group flex items-center justify-between rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100 transition hover:ring-teal-300 active:scale-[0.98]"
          >
            <div>
              <p className="text-sm font-bold text-slate-800">{t("history")}</p>
              <p className="mt-0.5 text-xs text-slate-400">{histCount} {t("viewed")}</p>
            </div>
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-500 transition group-hover:scale-110">
              <Clock3 className="h-5 w-5" />
            </span>
          </Link>
        </div>
      </motion.header>

      {/* ---------- MY ORDERS ---------- */}
      {features.orders !== false && (
        <Section
          tid="account-orders-section"
          title={t("tab_orders")}
          action={
            <Link data-testid="account-orders-viewall" to="/account/orders" className="flex items-center gap-0.5 text-xs font-semibold text-slate-500 hover:text-teal-700">
              {t("view_all")} <ChevronRight className="h-4 w-4" />
            </Link>
          }
        >
          <div className="grid grid-cols-5 rounded-2xl bg-white py-2 shadow-sm ring-1 ring-slate-100">
            {ORDER_GROUPS.map((g) => (
              <button
                key={g.key}
                data-testid={`order-group-${g.key}`}
                onClick={() => navigate(`/account/orders?status=${g.key}`)}
                className="relative flex flex-col items-center gap-1.5 py-2 transition active:scale-90"
              >
                <span className="relative text-slate-700">
                  <g.icon className="h-6 w-6" strokeWidth={1.7} />
                  <Badge n={groups.counts[g.key] || 0} />
                </span>
                <span className="text-[10px] font-semibold text-slate-600">{t(g.t)}</span>
              </button>
            ))}
          </div>

          {groups.latest && (
            <Link
              data-testid="account-latest-order"
              to={`/orders/${groups.latest.id || groups.latest.order_id}`}
              className="mt-3 flex items-center gap-3 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-slate-100 transition hover:ring-teal-300 active:scale-[0.99]"
            >
              <span className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-teal-50">
                {(groups.latest.product_image || groups.latest.image) && (
                  <img src={mediaUrl(groups.latest.product_image || groups.latest.image)} alt="" className="h-full w-full object-cover" loading="lazy" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-800">
                  {groups.latest.product_title || groups.latest.title || `${t("order_t")} #${String(groups.latest.id || groups.latest.order_id).slice(-8).toUpperCase()}`}
                </p>
                <p className="text-xs capitalize text-slate-400">{groups.latest.status || ""}</p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
            </Link>
          )}
        </Section>
      )}

      {/* ---------- MORE FEATURES ---------- */}
      <Section tid="account-features-section" title={t("more_features")}>
        <div className="grid grid-cols-3 gap-1 rounded-2xl bg-white py-3 shadow-sm ring-1 ring-slate-100 sm:grid-cols-5">
          <FeatureTile tid="feat-addresses" icon={MapPin} label={`${t("tab_addresses")}${addrCount ? ` (${addrCount})` : ""}`} onClick={() => navigate("/account/addresses")} />
          {auction?.enabled && (
            <FeatureTile tid="feat-auctions" icon={Gavel} label={t("nav_auctions")} onClick={() => navigate("/auctions")} />
          )}
          {features.rfq !== false && (
            <FeatureTile tid="feat-inquiries" icon={MessageCircle} label={`${t("tab_quotes")}${rfqs?.length ? ` (${rfqs.length})` : ""}`} onClick={() => navigate("/account/quotes")} />
          )}
          <FeatureTile tid="feat-invoices" icon={CreditCard} label={t("invoices")} onClick={() => navigate("/account/orders")} />
          <FeatureTile tid="feat-help" icon={HelpCircle} label={t("help_t")} onClick={() => navigate("/help")} />
          <FeatureTile
            tid="feat-sell"
            icon={Store}
            label={isSeller ? t("nav_dashboard") : t("start_selling")}
            onClick={() => navigate("/seller")}
          />
        </div>
        {unread > 0 && (
          <button
            data-testid="feat-unread-messages"
            onClick={() => navigate("/account/messages")}
            className="mt-3 flex w-full items-center justify-between rounded-2xl bg-teal-700 px-4 py-3 text-left text-white shadow-lg shadow-teal-700/25 transition active:scale-[0.99]"
          >
            <span className="flex items-center gap-2 text-sm font-bold">
              <MessageCircle className="h-4 w-4" /> {t("tab_messages")}
            </span>
            <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-extrabold text-teal-800">{unread} {t("unread")}</span>
          </button>
        )}
      </Section>

      {/* ---------- DOWNLOAD OUR APP (links come from admin dashboard) ---------- */}
      <motion.div {...fade(0.05)} className="mt-6">
        <DownloadApp compact />
      </motion.div>

      {/* ---------- START SELLING BANNER ---------- */}
      {!isSeller && (
        <motion.div {...fade(0.05)}>
          <Link
            data-testid="account-start-selling"
            to="/seller"
            className="mt-6 flex items-center justify-between gap-4 overflow-hidden rounded-2xl bg-gradient-to-r from-teal-700 to-teal-600 px-5 py-4 text-white shadow-lg shadow-teal-700/25 transition active:scale-[0.99]"
          >
            <div>
              <p className="font-heading text-base font-extrabold">{t("start_selling")}</p>
              <p className="mt-0.5 text-xs text-teal-100">{t("sell_col_sub")}</p>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0" />
          </Link>
        </motion.div>
      )}

      {/* ---------- FAVORITES PREVIEW ---------- */}
      {wishItems.length > 0 && (
        <Section
          tid="account-favorites-section"
          title={t("favorite")}
          action={
            <Link data-testid="account-favorites-viewall" to="/account/favorites" className="flex items-center gap-0.5 text-xs font-semibold text-slate-500 hover:text-teal-700">
              {t("view_all")} <ChevronRight className="h-4 w-4" />
            </Link>
          }
        >
          <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {wishItems.map((p) => (
              <Link
                key={p.id}
                data-testid={`fav-preview-${p.id}`}
                to={`/product/${p.id}`}
                className="w-32 shrink-0 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-100 transition hover:ring-teal-300"
              >
                <div className="aspect-square bg-teal-50">
                  {(p.thumb || p.image) && <img src={mediaUrl(p.thumb || p.image)} alt={p.title} loading="lazy" className="h-full w-full object-cover" />}
                </div>
                <p className="line-clamp-2 p-2.5 text-[11px] font-semibold leading-snug text-slate-700">{p.title}</p>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* ---------- INSPIRED BY YOUR INTERESTS ---------- */}
      <Inspired recs={recs} t={t} />
    </div>
  );
}

function Inspired({ recs, t }) {
  if (!recs.length) return null;
  return (
    <motion.section
      data-testid="account-inspired-section"
      className="mx-auto mt-8 max-w-3xl px-4 sm:px-6"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <h2 className="font-heading text-base md:text-lg font-bold text-slate-900">{t("inspired")}</h2>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {recs.slice(0, 6).map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </motion.section>
  );
}
